import formatMoney from '../../core/formatMoney';
import { selectNewFilter, getCurrentPage } from '../../sections/collection/filteredCollection';
import { handleize } from '../../core/helpers';
import { getURLFiltersAndSorting } from './filterURL';

/* IMPORTANT: Doesn't apply to the search overlay */

/**
 * Retrieve the previous filter state from localStorage if applicable.
 * 
 * @returns {Object|null} Previous filter state or null if not applicable
 */
const previousStateLocalStorage = () => {
  const saved_filters = JSON.parse(window.localStorage.getItem('wild-filter'));
  const previous_page = window.localStorage.getItem('previous-page') || '';
  // const current_path = window.location.pathname + window.location.search;

  // Extract pathname + search from previous_page (handles full URLs and relative paths)
  // let previous_full_path = previous_page;
  // try {
  //   const url = new URL(previous_page, window.location.origin);
  //   previous_full_path = url.pathname + url.search;
  // } catch (e) {
  //   // If previous_page is already a path, this will fail, so just use it as is
  // }

  console.log('[wild-filter]', saved_filters, previous_page/*, current_path, previous_full_path*/);

  // Check if previous_page is a product page or matches the current collection page
  // Detect if the user navigated back using the browser's back button
  const isBackNavigation = window.performance && window.performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
  const isProductPage = previous_page.includes('/products/');
  // const isSameCollectionPage = previous_full_path === current_path;

  // console.log(`[wild-filter] isProductPage: ${isProductPage}, isSameCollectionPage: ${isSameCollectionPage}`);

  // if ((isProductPage && isBackNavigation) || isSameCollectionPage) {
  if (isProductPage && isBackNavigation) {
    console.log('[wild-filter] apply');
    // window.localStorage.removeItem('wild-filter');
    return {
      saved_filters,
      previous_page,
    };
  }

  // saved_filters === null
  console.log('[wild-filter] don\'t apply');
  window.localStorage.removeItem('wild-filter');
  return null;
};

/**
 * Get the current sorting state based on the current page and previous state.
 * 
 * @param {boolean} with_previous_state - If true, will return the previous state if available
 * 
 * @returns {string} The current sorting state
 */
const getSortingState = (with_previous_state = false) => {
  const previous_state = (with_previous_state) ? previousStateLocalStorage() : null;

  const is_mobile = window.theme.breakpoints.current === 'mobile';
  const sorting_container = is_mobile ? document.querySelector('[data-mobile-filter-nav-options] [data-filter-options-type="sorting"]') : document.querySelector('[data-show-desktop-sort-nav]')?.parentElement?.querySelector('[data-sort-by]');
  const sortby_name = is_mobile ? 'sortby-mobile' : 'sortby';

  // Coming back from the PDP or refreshing the page
  if (previous_state !== null && previous_state?.previous_page.includes('/products/')) {
    return previous_state?.saved_filters?.sort_by || 'manual';
  }

  if (previous_state === null || !previous_state?.previous_page.includes('/products/')) {
    let sortby_value = '';
    const checked_input = sorting_container?.querySelector(`input[name="${sortby_name}"]:checked`);
    sortby_value = checked_input?.value || 'manual';

    // Assign the same sorting to desktop and mobile
    sorting_container?.querySelectorAll(`input[value="${sortby_value}"]`).forEach(input => {
      input.checked = true;
    });

    window.theme.collections.sorting = sortby_value;
    return sortby_value;
  }

  // Assign the same sorting to desktop and mobile
  sorting_container?.querySelectorAll(`input[value="${saved_filters.sort_by}"]`).forEach(input => {
    input.checked = true;
  });

  window.theme.collections.sorting = saved_filters.sort_by;
  return saved_filters.sort_by;
};

/**
 * Save the current filter state to localStorage every time a filter is selected
 * 
 * @returns {void}
 */
const saveFilterState = () => {
  if (!document.querySelector('body').classList.contains('template-plp')) {
    return;
  }

  window.localStorage.removeItem('wild-filter');

  const filter_options = {
    page: getCurrentPage(),
    sort_by: '',
  };

  filter_options.sort_by = getSortingState(true);

  // Push the selected option dynamically (since we now have dynamic filters)
  document.querySelectorAll('[data-filter-options-items]').forEach(option => {
    const active_options = option.querySelectorAll('[data-clickable-option].active');

    if (active_options.length) {
      const group_name = option.getAttribute('data-filter-options-type');

      active_options.forEach(active_option => {
        if (typeof filter_options[group_name] === 'undefined') {
          filter_options[group_name] = [];
        }

        const add_value = active_option.getAttribute('data-option-value');
        // We can have duplicates since we have multiple filter components on the page
        if (!filter_options[group_name].includes(add_value)) {
          filter_options[group_name].push(add_value);
        }
      });
    }
  });

  window.localStorage.setItem('wild-filter', JSON.stringify(filter_options));
};

/**
 * Get the current pagination state from localStorage (current page number)
 * 
 * @returns {number} Current page number
 */
const getPaginationState = () => {
  const saved_filters = JSON.parse(window.localStorage.getItem('wild-filter'));

  if (saved_filters === null) {
    return 1;
  }

  return saved_filters.page;
};

/**
 * Apply the saved filters when coming back from the product page OR if filter query strings is in URL OR after selecting it from the sort by feature
 * Only called from loadCollection() in filtering.js
 * 
 * @param {boolean} new_nosto_event - If true, will trigger a new Nosto request after applying the filters
 * 
 * @returns {Promise<boolean>} - Returns true if filters were applied, false otherwise
 */
/**
 * Where the filters to restore come from: the URL query string first, the saved state second.
 *
 * Side-effect free, so it can be asked the same question twice — once up front to predict
 * whether a second Nosto request is coming, once for real inside applyFilterState().
 *
 * @returns {{saved_filters: Object, from_url: boolean}|null}
 */
const resolvePreviousFilters = (validate_against_dom = true) => {
  const url_filters_and_sorting = getURLFiltersAndSorting(validate_against_dom);

  if (url_filters_and_sorting !== null) {
    return { saved_filters: url_filters_and_sorting, from_url: true };
  }

  const previous_state = previousStateLocalStorage();
  return (previous_state === null) ? null : { ...previous_state, from_url: false };
};

/**
 * Will applyFilterState() fire a Nosto request of its own?
 *
 * loadCollection() asks this before its own request so it can skip rendering a grid that is
 * about to be thrown away. It deliberately mirrors the decision applyFilterState() makes below
 * and must keep mirroring it — if the two disagree and this says false when a refetch does
 * happen, the shopper gets a flash of unfiltered products; if it says true when none happens,
 * they are left on placeholders.
 *
 * @returns {boolean}
 */
const willRefetchOnApply = () => {
  // Runs before the first response, so the filter menu doesn't exist to validate against yet
  const previous_filters = resolvePreviousFilters(false);

  if (previous_filters === null || !previous_filters.saved_filters) {
    return false;
  }

  const saved = previous_filters.saved_filters;
  const saved_page = (typeof saved.page === 'undefined') ? 1 : saved.page;
  const sorting_state = saved.sort_by || getSortingState();
  const filter_keys = Object.keys(saved).filter(key => key !== 'page' && key !== 'sort_by');

  return !(saved_page === 1 && sorting_state === 'manual' && filter_keys.length === 0);
};

/**
 * The filter state that is about to be applied, in the shape the Nosto request wants, resolved
 * straight from the URL / saved state without going through the DOM.
 *
 * applyFilterState() normally paints the state into the filter menu and lets selectNewFilter()
 * read it back out, but the menu is built from facets that only arrive with the first response.
 * This skips that round trip so the filtered request can be started immediately.
 *
 * @returns {{filtering: Array, sorting: string, page: number}|null}
 */
const getPendingFilterState = () => {
  // Same timing as willRefetchOnApply(): no filter menu yet, so no DOM validation
  const previous_filters = resolvePreviousFilters(false);

  if (previous_filters === null || !previous_filters.saved_filters) {
    return null;
  }

  const saved = previous_filters.saved_filters;

  // { "skus.customFields.size": ["M"] } -> [["skus.customFields.size", "M"]]
  // the same pairs retrieveSelectedFilters() builds from the DOM
  const filtering = Object.entries(saved)
    .filter(([key]) => key !== 'page' && key !== 'sort_by')
    .flatMap(([key, values]) => (Array.isArray(values) ? values.map(value => [key, value]) : []));

  return {
    filtering,
    sorting: saved.sort_by || getSortingState(),
    page: (typeof saved.page === 'undefined') ? 1 : saved.page,
  };
};

const applyFilterState = async (new_nosto_event = true) => {
  const previous_filters = resolvePreviousFilters();

  if (previous_filters?.from_url) {
    // Save the URL filters to localStorage for future use
    window.localStorage.setItem('wild-filter', JSON.stringify(previous_filters.saved_filters));
    console.log('[wild-filter] applyFilterState query string:', previous_filters.saved_filters);
  } else {
    console.log('[wild-filter] applyFilterState local storage:', previous_filters);
  }

  if (previous_filters === null || previous_filters?.saved_filters === null) {
    return false;
  }

  document.querySelectorAll('[data-selected-filters-list] [data-selected-filters-item]').forEach(item => item.remove());

  // Re-select the filter options based on the saved state
  for (const saved_filter in previous_filters.saved_filters) {
    if (saved_filter === 'page' || saved_filter === 'sort_by') continue;

    const filterOptionsContainers = document.querySelectorAll(`[data-filter-options-type="${saved_filter}"]`);
    if (!filterOptionsContainers.length) continue;

    const saved_options = previous_filters.saved_filters[saved_filter];

    filterOptionsContainers.forEach(container => {
      const clickableOptions = container.querySelectorAll('[data-clickable-option]');
      clickableOptions.forEach(option => {
        option.classList.remove('active');
        option.setAttribute('aria-checked', 'false');
        option.parentElement?.classList.remove('active');

        const adaInput = option.querySelector('[data-option-ada]');
        if (adaInput) {
          adaInput.checked = false;
        }

        const optionValue = option.getAttribute('data-option-value');
        if (Array.isArray(saved_options) && saved_options.includes(optionValue)) {
          option.classList.add('active');
          option.setAttribute('aria-checked', 'true');
          option.parentElement?.classList.add('active');

          if (adaInput) {
            adaInput.checked = true;
          }

          let saved_option_value = optionValue;
          if (saved_option_value.includes('vt::')) {
            saved_option_value = saved_option_value.split('::').pop();
          }

          if (saved_filter === 'price') {
            const price_range_parts = saved_option_value.split('-');
            saved_option_value = `${formatMoney(Math.ceil(Number(price_range_parts[0]) * window.Shopify.currency.rate) * 100, theme.moneyFormat).replace('.00', '').replace(',00', '')}-${formatMoney(Math.ceil(Number(price_range_parts[1]) * window.Shopify.currency.rate) * 100, theme.moneyFormat).replace('.00', '').replace(',00', '')}`;
          }

          // Always add the selected filter item
          const selected_filter_item_html = `
            <div class="selected-filters__item no-themed"
              data-selected-filters-item
              data-selected-filters-short-value="${handleize(optionValue)}"
              data-selected-filters-type="${saved_filter}"
              data-selected-filters-value="${optionValue}"
              role="button"
              tabindex="0">
              ${saved_option_value}
            </div>
          `;

          const selectedFiltersLists = document.querySelectorAll('[data-selected-filters-list]');
          selectedFiltersLists.forEach((filtersList) => {
            const selected_filter_item_selector = `[data-selected-filters-type="${saved_filter}"][data-selected-filters-value="${optionValue}"]`;
            if (filtersList.querySelector(selected_filter_item_selector)) {
              return;
            }

            const clearAllFilter = filtersList.querySelector('[data-clear-all-filters]');

            if (clearAllFilter) {
              clearAllFilter.insertAdjacentHTML('beforebegin', selected_filter_item_html);
              return;
            }

            filtersList.insertAdjacentHTML('beforeend', selected_filter_item_html);
          });
        }
      });
    });
  }

  // Re-select the sorting option based on the saved state
  const sorting_state = previous_filters?.saved_filters?.sort_by || getSortingState();
  document.querySelectorAll(`[data-sort-by] input[value="${sorting_state}"]`).forEach(input => {
    input.checked = true;
  });

  ['#filters-panel-v3', '[data-mobile-filter-nav-options]'].forEach((sort_root_selector) => {
    const selected_sort_element = document.querySelector(`${sort_root_selector} [data-selected-sort]`);
    if (!selected_sort_element) return;

    const selected_input = document.querySelector(`${sort_root_selector} [data-sort-by] input[value="${sorting_state}"]`) || document.querySelector(`${sort_root_selector} [data-sort-by] input:checked`);

    const selected_sort_label = selected_input
      ?.closest('label')
      ?.querySelector('.custom-radio__label')
      ?.textContent
      ?.trim() || '';

    selected_sort_element.textContent = selected_sort_label;
  });

  document.querySelectorAll('[data-selected-filters]').forEach((filter_container) => {
    const has_selected_filters = filter_container.querySelectorAll('[data-selected-filters-item]').length > 0;
    filter_container.classList.toggle('active', has_selected_filters);
  });

  // Apply the filters in Nosto
  if (new_nosto_event) {
    const saved_page = (typeof previous_filters?.saved_filters?.page === 'undefined') ? 1 : previous_filters?.saved_filters?.page;
    window.theme.collections.page = saved_page;
    const sorting_state = previous_filters?.saved_filters?.sort_by || getSortingState();
    // problem: we don't have the sorting when coming back from pdp
    window.theme.collections.sorting = sorting_state;

    /*
    {
        "page": 1,
        "sort_by": "manual"
    }
    
    {
      "saved_filters": {
      "page": 1,
      "sort_by": "manual",
      "skus.customFields.astr-color_family": [
        "black",
        "blue"
      ],
      "skus.customFields.size": [
        "M"
      ]
    }
    */
    const filterKeys = Object.keys(previous_filters?.saved_filters || {}).filter(
      key => key !== 'page' && key !== 'sort_by'
    );

    console.log('saved_page', saved_page, 'sorting_state', sorting_state, 'previous_filters?.saved_filters', previous_filters?.saved_filters);
    if (saved_page === 1 && sorting_state === 'manual' && filterKeys.length === 0) {
      // If we are on the first page and sorting is manual with no filters applied, we don't need to load the collection again
      console.log('[wild-filter] No need to load collection again');
      return true;
    }

    if (saved_page > 1) {
      await selectNewFilter(false, sorting_state, saved_page);
    } else {
      await selectNewFilter(false, sorting_state);
    }
  }

  return true;
};

export {
  saveFilterState,
  applyFilterState,
  willRefetchOnApply,
  getPendingFilterState,
  getSortingState,
  getPaginationState,
};
